import { useCallback, useEffect, useRef, type RefObject } from "react";

/**
 * RL-CAN-34 — the FD-CAN-38 recreation's scroll-driven effects as ONE hook
 * for any scroller: the fade edges that follow the scroll, and the rows that
 * settle in as they enter the frame. `AnimatedList` wears it; so may the
 * Context Rail's windowed tree and paged rows, whose DOM rows come and go as
 * the person scrolls (a MutationObserver keeps newly mounted rows observed,
 * so ten thousand rows in a windowed frame settle the same way ten do).
 *
 * The hook writes straight to the elements — never a render per scroll —
 * and mounts nothing itself: the wearer renders the scroller and the two
 * fade elements and hands their refs over. Reduced motion stills the settle
 * (the stylesheet) and keeps the fades static (this hook skips the scroll
 * painting and leaves both fades at their resting opacity).
 */
export interface AnimatedFrameOptions {
  /** The settle-in effect over the rows (default on). */
  readonly settle?: boolean;
  /** Travel (px) over which a fade goes from 0 to 1 (default 48). */
  readonly fadeDistancePx?: number;
  /** Selector for the rows the settle observes (default `[data-alist-item]`). */
  readonly rowSelector?: string;
  /** Re-observe the rows when this value changes (the item set). */
  readonly rows?: unknown;
}

export interface AnimatedFrame {
  readonly topFadeRef: RefObject<HTMLDivElement | null>;
  readonly bottomFadeRef: RefObject<HTMLDivElement | null>;
  /** Repaint the fades now — after a programmatic scroll, or a layout change the observers miss. */
  readonly paint: () => void;
}

const SETTLE_THRESHOLDS = [0, 0.2, 0.4, 0.6, 0.8, 1];
const DEFAULT_FADE_DISTANCE_PX = 48;
const DEFAULT_ROW_SELECTOR = "[data-alist-item]";

export function prefersReducedMotion(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** (1) The fade edges follow the scroll — written straight to the elements. */
export function paintFrameFades(
  scroller: HTMLElement | null,
  topFade: HTMLElement | null,
  bottomFade: HTMLElement | null,
  fadeDistancePx: number = DEFAULT_FADE_DISTANCE_PX,
): void {
  if (scroller === null) return;
  const { scrollTop, scrollHeight, clientHeight } = scroller;
  if (topFade !== null) {
    topFade.style.opacity = String(Math.min(scrollTop / fadeDistancePx, 1));
  }
  if (bottomFade !== null) {
    const remaining = Math.max(scrollHeight - scrollTop - clientHeight, 0);
    bottomFade.style.opacity =
      scrollHeight <= clientHeight + 1 ? "0" : String(Math.min(remaining / fadeDistancePx, 1));
  }
}

export function useAnimatedFrame<T extends HTMLElement>(
  scrollerRef: RefObject<T | null>,
  options: AnimatedFrameOptions = {},
): AnimatedFrame {
  const {
    settle = true,
    fadeDistancePx = DEFAULT_FADE_DISTANCE_PX,
    rowSelector = DEFAULT_ROW_SELECTOR,
    rows,
  } = options;
  const topFadeRef = useRef<HTMLDivElement | null>(null);
  const bottomFadeRef = useRef<HTMLDivElement | null>(null);

  const paint = useCallback(() => {
    paintFrameFades(scrollerRef.current, topFadeRef.current, bottomFadeRef.current, fadeDistancePx);
  }, [fadeDistancePx, scrollerRef]);

  // (1) The fades: on scroll, on resize, and on every change of the row set.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller === null) return;
    paint();
    if (prefersReducedMotion()) return;
    const onScroll = () => paint();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    const resize =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => paint());
    resize?.observe(scroller);
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      resize?.disconnect();
    };
  }, [paint, rows, scrollerRef]);

  // (2) The rows settle in as they enter the frame; rows that mount later
  // (a windowed tree, a paged list) are observed as they arrive.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!settle || scroller === null || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target instanceof HTMLElement) {
            entry.target.style.setProperty("--vis", entry.intersectionRatio.toFixed(2));
          }
        }
      },
      { root: scroller, threshold: SETTLE_THRESHOLDS },
    );
    const observeAll = () => {
      for (const row of scroller.querySelectorAll<HTMLElement>(rowSelector)) observer.observe(row);
    };
    observeAll();
    const mutations =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver((records) => {
            for (const record of records) {
              for (const node of record.addedNodes) {
                if (!(node instanceof HTMLElement)) continue;
                if (node.matches(rowSelector)) observer.observe(node);
                for (const row of node.querySelectorAll<HTMLElement>(rowSelector)) observer.observe(row);
              }
              for (const node of record.removedNodes) {
                if (!(node instanceof HTMLElement)) continue;
                if (node.matches(rowSelector)) observer.unobserve(node);
                for (const row of node.querySelectorAll<HTMLElement>(rowSelector)) observer.unobserve(row);
              }
            }
          });
    mutations?.observe(scroller, { childList: true, subtree: true });
    return () => {
      mutations?.disconnect();
      observer.disconnect();
    };
  }, [rowSelector, rows, scrollerRef, settle]);

  return { topFadeRef, bottomFadeRef, paint };
}
